/**
 * ResultadosEstudiosPanel — Captura de resultados de estudios traídos por el paciente.
 * - Subida de imagen (foto/escaneo del resultado)
 * - OCR con IA → extrae parámetros estructurados
 * - UI editable (el médico valida/corrige antes de guardar)
 */
import React, { useState } from 'react';
import {
  Box, Typography, Button, Chip, IconButton, TextField, CircularProgress, Tooltip,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Science as ScienceIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  TrendingUp as AltoIcon,
  TrendingDown as BajoIcon,
  Remove as NormalIcon,
  AddCircle as AddIcon,
  AttachFile as AttachIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
} from '@mui/icons-material';

const API_URL = '/api';

const ESTADO_META = {
  alto:   { color: '#ef4444', bg: '#fef2f2', label: '↑ Alto',   Icon: AltoIcon },
  bajo:   { color: '#3b82f6', bg: '#eff6ff', label: '↓ Bajo',   Icon: BajoIcon },
  normal: { color: '#10b981', bg: '#ecfdf5', label: 'Normal',   Icon: NormalIcon },
};

export default function ResultadosEstudiosPanel({ estudios = [], onChange, contextoClinico = '', expedienteId = null }) {
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState('');
  const userId = localStorage.getItem('user_id');

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      if (contextoClinico) fd.append('contexto_clinico', contextoClinico);
      if (expedienteId) fd.append('expediente_id', String(expedienteId));
      const res = await fetch(`${API_URL}/emr/ia/ocr-resultados-estudios`, {
        method: 'POST',
        headers: { 'X-User-Id': userId },
        body: fd,
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`HTTP ${res.status}: ${txt.substring(0, 200)}`);
      }
      const data = await res.json();
      const nuevos = (data.estudios || []).map(est => ({
        ...est,
        _id: `est_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
        fecha_revision: new Date().toISOString().slice(0, 10),
      }));
      onChange([...estudios, ...nuevos]);
    } catch (err) {
      console.error('[OCR] Error:', err);
      setError(err.message || 'Error procesando el archivo');
    } finally {
      setSubiendo(false);
      e.target.value = '';
    }
  };

  const eliminarEstudio = (id) => {
    onChange(estudios.filter(e => e._id !== id));
  };

  const actualizarEstudio = (id, cambios) => {
    onChange(estudios.map(e => e._id === id ? { ...e, ...cambios } : e));
  };

  const agregarEstudioManual = () => {
    onChange([...estudios, {
      _id: `est_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
      nombre: '',
      fecha: null,
      laboratorio: null,
      parametros: [],
      interpretacion_breve: '',
      fecha_revision: new Date().toISOString().slice(0, 10),
    }]);
  };

  return (
    <Box>
      {/* Header con upload */}
      <Box sx={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 1.5,
        p: 1.5, borderRadius: 2,
        background: 'linear-gradient(135deg, #ecfeff 0%, #f0f9ff 100%)',
        border: '1px solid #a5f3fc',
      }}>
        <ScienceIcon sx={{ fontSize: 22, color: '#0891b2' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#0c4a6e', textTransform: 'uppercase', letterSpacing: 0.4 }}>
            Resultados traídos por el paciente
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#075985' }}>
            Subí foto, escaneo o PDF — la IA extrae automáticamente todos los parámetros
          </Typography>
        </Box>
        <Button
          component="label"
          size="small"
          variant="contained"
          disabled={subiendo}
          startIcon={subiendo ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <UploadIcon sx={{ fontSize: 16 }} />}
          sx={{
            textTransform: 'none', fontWeight: 700, fontSize: 11, borderRadius: 2,
            background: '#0891b2', boxShadow: '0 4px 10px rgba(8, 145, 178, 0.3)',
            '&:hover': { background: '#0e7490' },
          }}
        >
          {subiendo ? 'Procesando...' : 'Subir resultado'}
          <input type="file" accept="image/*,application/pdf,.pdf" hidden onChange={handleUpload} />
        </Button>
        <Button
          size="small"
          variant="outlined"
          startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          onClick={agregarEstudioManual}
          sx={{
            textTransform: 'none', fontWeight: 600, fontSize: 11, borderRadius: 2,
            color: '#0891b2', borderColor: '#67e8f9',
            '&:hover': { borderColor: '#0891b2', background: '#ecfeff' },
          }}
        >
          Manual
        </Button>
      </Box>

      {error && (
        <Box sx={{ p: 1, mb: 1, borderRadius: 1.5, background: '#fef2f2', border: '1px solid #fca5a5' }}>
          <Typography sx={{ fontSize: 11.5, color: '#b91c1c' }}>⚠ {error}</Typography>
        </Box>
      )}

      {estudios.length === 0 && !subiendo && (
        <Box sx={{
          p: 2, borderRadius: 2, textAlign: 'center',
          background: '#f8fafc', border: '1px dashed #cbd5e1',
        }}>
          <ScienceIcon sx={{ fontSize: 28, color: '#cbd5e1', mb: 0.5 }} />
          <Typography sx={{ fontSize: 12, color: '#64748b' }}>
            Sin resultados cargados. Subí una foto del estudio o agregá uno manual.
          </Typography>
        </Box>
      )}

      {/* Lista de estudios agrupados por archivo de origen */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {(() => {
          // Agrupar estudios por archivo_url (null/undefined = "sin archivo origen")
          const grupos = new Map();
          for (const est of estudios) {
            const key = est.archivo_url || '__manual__';
            if (!grupos.has(key)) {
              grupos.set(key, {
                archivo_url: est.archivo_url || null,
                archivo_nombre: est.archivo_nombre || null,
                archivo_tipo: est.archivo_tipo || null,
                estudios: [],
              });
            }
            grupos.get(key).estudios.push(est);
          }
          // Mover el grupo "manual" al final
          const grupoOrdenados = [...grupos.values()].sort((a, b) => {
            if (!a.archivo_url && b.archivo_url) return 1;
            if (a.archivo_url && !b.archivo_url) return -1;
            return 0;
          });
          return grupoOrdenados.map((grupo, gi) => (
            <Box key={grupo.archivo_url || `manual-${gi}`} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {/* Banner del archivo de origen */}
              <ArchivoBanner grupo={grupo} />
              {/* Estudios del grupo */}
              {grupo.estudios.map((est) => (
                <EstudioCard
                  key={est._id}
                  estudio={est}
                  onChange={(cambios) => actualizarEstudio(est._id, cambios)}
                  onDelete={() => eliminarEstudio(est._id)}
                />
              ))}
            </Box>
          ));
        })()}
      </Box>
    </Box>
  );
}

function ArchivoBanner({ grupo }) {
  const esManual = !grupo.archivo_url;
  const esPdf = grupo.archivo_tipo === 'application/pdf';
  const cantidad = grupo.estudios.length;

  if (esManual) {
    return (
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.7,
        px: 1, py: 0.7, borderRadius: 1.5,
        background: '#f1f5f9', border: '1px dashed #cbd5e1',
      }}>
        <EditIcon sx={{ fontSize: 14, color: '#64748b' }} />
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>
          Sin archivo origen · {cantidad} estudio{cantidad !== 1 ? 's' : ''} (capturado a mano)
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      component="a"
      href={`${API_URL}${grupo.archivo_url}`}
      target="_blank"
      rel="noopener noreferrer"
      sx={{
        display: 'flex', alignItems: 'center', gap: 0.8,
        px: 1.2, py: 0.8, borderRadius: 1.5,
        background: esPdf
          ? 'linear-gradient(135deg, #fef2f2 0%, #fff7ed 100%)'
          : 'linear-gradient(135deg, #ecfeff 0%, #eff6ff 100%)',
        border: `1px solid ${esPdf ? '#fecaca' : '#a5f3fc'}`,
        textDecoration: 'none',
        transition: 'all 0.15s',
        cursor: 'pointer',
        '&:hover': {
          filter: 'brightness(0.97)',
          boxShadow: `0 2px 8px ${esPdf ? 'rgba(239, 68, 68, 0.15)' : 'rgba(8, 145, 178, 0.15)'}`,
        },
      }}
    >
      {esPdf
        ? <PdfIcon sx={{ fontSize: 18, color: '#dc2626' }} />
        : <ImageIcon sx={{ fontSize: 18, color: '#0891b2' }} />}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{
          fontSize: 11.5, fontWeight: 800,
          color: esPdf ? '#991b1b' : '#0c4a6e',
          textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap',
        }}>
          {grupo.archivo_nombre || 'Archivo subido'}
        </Typography>
        <Typography sx={{ fontSize: 10, color: '#64748b', mt: 0.1 }}>
          {cantidad} estudio{cantidad !== 1 ? 's' : ''} extraído{cantidad !== 1 ? 's' : ''} · Click para abrir el original
        </Typography>
      </Box>
      <Typography sx={{
        fontSize: 10, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase',
        color: esPdf ? '#dc2626' : '#0891b2',
        flexShrink: 0,
      }}>
        Ver →
      </Typography>
    </Box>
  );
}

function EstudioCard({ estudio, onChange, onDelete }) {
  const [editando, setEditando] = useState(!estudio.nombre);
  const parametros = estudio.parametros || [];
  const anormales = parametros.filter(p => p.estado === 'alto' || p.estado === 'bajo').length;

  const updateParametro = (idx, cambios) => {
    const nuevos = parametros.map((p, i) => i === idx ? { ...p, ...cambios } : p);
    onChange({ parametros: nuevos });
  };

  const eliminarParametro = (idx) => {
    onChange({ parametros: parametros.filter((_, i) => i !== idx) });
  };

  const agregarParametro = () => {
    onChange({
      parametros: [...parametros, {
        nombre: '', valor: '', unidad: '', referencia: '', estado: 'normal',
      }],
    });
  };

  return (
    <Box sx={{
      p: 1.2, borderRadius: 2,
      background: '#fff',
      border: '1px solid #e2e8f0',
      borderLeft: anormales > 0 ? '3px solid #ef4444' : '3px solid #10b981',
    }}>
      {/* Header del estudio */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7, flexWrap: 'wrap', mb: 0.7 }}>
        {editando ? (
          <TextField
            size="small" autoFocus
            placeholder="Nombre del estudio (ej: Biometría hemática completa)"
            value={estudio.nombre || ''}
            onChange={(e) => onChange({ nombre: e.target.value })}
            sx={{
              flex: 1, minWidth: 200,
              '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: 12.5, fontWeight: 700 },
            }}
          />
        ) : (
          <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0f172a', flex: 1, minWidth: 200 }}>
            {estudio.nombre || 'Estudio sin nombre'}
          </Typography>
        )}
        {estudio.fecha && (
          <Chip
            label={estudio.fecha} size="small"
            sx={{ fontSize: 10, height: 20, background: '#eef2ff', color: '#4338ca', fontWeight: 700 }}
          />
        )}
        {anormales > 0 && (
          <Chip
            label={`${anormales} anormal${anormales > 1 ? 'es' : ''}`}
            size="small"
            sx={{ fontSize: 10, height: 20, background: '#fef2f2', color: '#b91c1c', fontWeight: 700 }}
          />
        )}
        <Tooltip title={editando ? 'Listo' : 'Editar nombre del estudio'}>
          <IconButton size="small" onClick={() => setEditando(!editando)} sx={{ color: '#64748b' }}>
            {editando ? <CheckIcon sx={{ fontSize: 16 }} /> : <EditIcon sx={{ fontSize: 14 }} />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Eliminar estudio">
          <IconButton size="small" onClick={onDelete} sx={{ color: '#ef4444' }}>
            <DeleteIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {estudio.laboratorio && (
        <Typography sx={{ fontSize: 10.5, color: '#64748b', mb: 0.5 }}>
          {estudio.laboratorio}
        </Typography>
      )}

      {/* Tabla de parámetros */}
      {parametros.length > 0 && (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 0.8fr 1.2fr 0.8fr auto',
          gap: 0.4,
          mb: 0.5,
          alignItems: 'center',
        }}>
          {/* Header */}
          <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
            Parámetro
          </Typography>
          <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
            Valor
          </Typography>
          <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
            Unidad
          </Typography>
          <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
            Referencia
          </Typography>
          <Typography sx={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
            Estado
          </Typography>
          <span></span>

          {parametros.map((p, idx) => {
            const meta = ESTADO_META[p.estado] || ESTADO_META.normal;
            return (
              <React.Fragment key={idx}>
                <ParamInput
                  value={p.nombre}
                  onChange={(v) => updateParametro(idx, { nombre: v })}
                  placeholder="Hemoglobina"
                  bold
                />
                <ParamInput
                  value={p.valor}
                  onChange={(v) => updateParametro(idx, { valor: v })}
                  placeholder="13.5"
                  color={meta.color}
                  bold
                />
                <ParamInput
                  value={p.unidad}
                  onChange={(v) => updateParametro(idx, { unidad: v })}
                  placeholder="g/dL"
                />
                <ParamInput
                  value={p.referencia}
                  onChange={(v) => updateParametro(idx, { referencia: v })}
                  placeholder="12-16"
                />
                {/* Selector de estado */}
                <Box sx={{ display: 'flex', gap: 0.2 }}>
                  {Object.entries(ESTADO_META).map(([key, m]) => (
                    <Tooltip key={key} title={m.label}>
                      <Box
                        onClick={() => updateParametro(idx, { estado: key })}
                        sx={{
                          width: 18, height: 18, borderRadius: '50%',
                          cursor: 'pointer',
                          background: p.estado === key ? m.color : '#fff',
                          border: `1.5px solid ${m.color}`,
                          opacity: p.estado === key ? 1 : 0.45,
                          transition: 'all 0.15s',
                          '&:hover': { opacity: 1 },
                        }}
                      />
                    </Tooltip>
                  ))}
                </Box>
                <IconButton size="small" onClick={() => eliminarParametro(idx)} sx={{ color: '#cbd5e1', '&:hover': { color: '#ef4444' } }}>
                  <CloseIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </React.Fragment>
            );
          })}
        </Box>
      )}

      <Button
        size="small"
        startIcon={<AddIcon sx={{ fontSize: 14 }} />}
        onClick={agregarParametro}
        sx={{
          textTransform: 'none', fontWeight: 600, fontSize: 10.5,
          color: '#64748b', mt: 0.3,
          '&:hover': { color: '#0891b2', background: 'transparent' },
        }}
      >
        Agregar parámetro
      </Button>

      {/* Interpretación */}
      <TextField
        fullWidth size="small"
        placeholder="Interpretación clínica de los hallazgos..."
        value={estudio.interpretacion_breve || ''}
        onChange={(e) => onChange({ interpretacion_breve: e.target.value })}
        multiline minRows={1} maxRows={3}
        sx={{
          mt: 0.7,
          '& .MuiOutlinedInput-root': {
            borderRadius: 1.5, fontSize: 11.5, background: '#f8fafc',
          },
        }}
      />
    </Box>
  );
}

function ParamInput({ value, onChange, placeholder, color, bold }) {
  return (
    <TextField
      size="small"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      sx={{
        '& .MuiOutlinedInput-root': {
          borderRadius: 1, fontSize: 11.5,
          background: '#fff',
          '& input': {
            padding: '4px 6px',
            fontWeight: bold ? 700 : 400,
            color: color || '#0f172a',
          },
          '& fieldset': { borderColor: '#e2e8f0' },
        },
      }}
    />
  );
}
